import {
  buildSearchExpressionStatePatch,
  normalizeRawSearchExpressionTree,
  parseSearchExpressionDslResult,
  resolveSearchExpression,
  serializeSearchExpressionDsl,
} from "./map-search-contract.js";

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function parseLayersParam(value) {
  if (value == null) {
    return null;
  }
  const seen = new Set();
  const layers = [];
  for (const part of String(value ?? "").split(/[\s,]+/g)) {
    const layerId = part.trim().toLowerCase();
    if (!layerId || seen.has(layerId)) {
      continue;
    }
    seen.add(layerId);
    layers.push(layerId);
  }
  return layers;
}

function parseViewMode(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "2d" || normalized === "3d") {
    return normalized;
  }
  return null;
}

function stripEmptyBranches(patch) {
  if (!isPlainObject(patch)) {
    return null;
  }
  if (isPlainObject(patch._map_ui?.search) && !Object.keys(patch._map_ui.search).length) {
    delete patch._map_ui.search;
  }
  if (isPlainObject(patch._map_ui) && !Object.keys(patch._map_ui).length) {
    delete patch._map_ui;
  }
  if (isPlainObject(patch._map_bridged?.filters) && !Object.keys(patch._map_bridged.filters).length) {
    delete patch._map_bridged.filters;
  }
  if (isPlainObject(patch._map_bridged?.ui) && !Object.keys(patch._map_bridged.ui).length) {
    delete patch._map_bridged.ui;
  }
  if (isPlainObject(patch._map_bridged) && !Object.keys(patch._map_bridged).length) {
    delete patch._map_bridged;
  }
  return Object.keys(patch).length ? patch : null;
}

function cloneSearchExpressionIssues(issues) {
  return Array.isArray(issues)
    ? issues
        .filter((issue) => isPlainObject(issue))
        .map((issue) => JSON.parse(JSON.stringify(issue)))
    : [];
}

export function parseQuerySignalPatch(locationHref = globalThis.location?.href) {
  if (!locationHref) {
    return null;
  }

  let params;
  try {
    params = new URL(locationHref, "https://fishystuff.fish").searchParams;
  } catch (_error) {
    return null;
  }

  const expr = params.get("expr");
  const hasExpr = expr != null;
  const layers = parseLayersParam(params.get("layers")) ?? [];
  const viewMode = parseViewMode(params.get("view"));

  /** @type {Record<string, unknown>} */
  const patch = {};

  const parsedExpressionResult = hasExpr
    ? parseSearchExpressionDslResult(expr)
    : { expression: null, rawExpressionTree: null, issues: [] };
  const parsedExpression = parsedExpressionResult.expression;
  const rawExpressionTree = normalizeRawSearchExpressionTree(
    parsedExpressionResult.rawExpressionTree,
  );
  const expressionIssues = cloneSearchExpressionIssues(parsedExpressionResult.issues);
  if (parsedExpression) {
    const searchSelectionPatch = buildSearchExpressionStatePatch(parsedExpression);
    patch._map_ui = {
      ...(isPlainObject(patch._map_ui) ? patch._map_ui : {}),
      ...(isPlainObject(searchSelectionPatch._map_ui) ? searchSelectionPatch._map_ui : {}),
      search: {
        ...(isPlainObject(searchSelectionPatch._map_ui?.search)
          ? searchSelectionPatch._map_ui.search
          : {}),
        ...(isPlainObject(patch._map_ui?.search) ? patch._map_ui.search : {}),
      },
    };
    patch._map_bridged = {
      ...(isPlainObject(searchSelectionPatch._map_bridged)
        ? searchSelectionPatch._map_bridged
        : {}),
    };
  }

  if (hasExpr && (expressionIssues.length || (!parsedExpression && rawExpressionTree))) {
    patch._map_ui = patch._map_ui || {};
    patch._map_ui.search = {
      ...(isPlainObject(patch._map_ui.search) ? patch._map_ui.search : {}),
      rawExpression: String(expr ?? ""),
      rawExpressionTree,
      expressionIssues,
    };
  }

  if (
    layers.length
  ) {
    patch._map_bridged = patch._map_bridged || {};
    patch._map_bridged.filters = patch._map_bridged.filters || {};
    if (layers.length) {
      patch._map_bridged.filters.layerIdsVisible = layers;
    }
  }

  if (viewMode != null) {
    patch._map_bridged = patch._map_bridged || {};
    patch._map_bridged.ui = {};
    if (viewMode != null) {
      patch._map_bridged.ui.viewMode = viewMode;
    }
  }

  return stripEmptyBranches(patch);
}

function deleteSearchExpressionParams(params) {
  for (const key of [
    "expr",
    "search",
    "focusFish",
    "fish",
    "fishTerms",
    "fishFilterTerms",
    "patch",
    "fromPatch",
    "patchFrom",
    "toPatch",
    "untilPatch",
    "patchTo",
  ]) {
    params.delete(key);
  }
}

function hasActiveSearchExpression(expression) {
  if (!isPlainObject(expression)) {
    return false;
  }
  if (expression.type === "term") {
    return true;
  }
  if (expression.type === "container") {
    return hasActiveSearchExpression(expression.expression);
  }
  return Array.isArray(expression.children) && expression.children.length > 0;
}

export function buildCanonicalMapSearchParams(signals) {
  const params = new URLSearchParams();
  const search = isPlainObject(signals?._map_ui?.search) ? signals._map_ui.search : {};
  const expression = resolveSearchExpression(search.expression);
  if (hasActiveSearchExpression(expression)) {
    const expr = serializeSearchExpressionDsl(expression);
    if (expr) {
      params.set("expr", expr);
    }
  }
  return params;
}

export function buildCanonicalMapUrl(
  signals,
  { baseHref = globalThis.location?.href || "https://fishystuff.fish/map/" } = {},
) {
  let url;
  try {
    url = new URL(baseHref, "https://fishystuff.fish");
  } catch (_error) {
    url = new URL("https://fishystuff.fish/map/");
  }
  deleteSearchExpressionParams(url.searchParams);
  const canonicalParams = buildCanonicalMapSearchParams(signals);
  const expr = canonicalParams.get("expr");
  if (expr) {
    url.searchParams.set("expr", expr);
  }
  return url.toString();
}
